import { useToastContext } from "@ttc3k/trekker";
import { useAppContext } from "AppContext";
import {
  EnumEntityReferenceType,
  EnumUserAccessOperatorsRole,
  EnumUserAccessOperatorsStatus,
  OperatorManyUseCaseEnum,
  PublicationStatusType,
  useOperatorManyLazyQuery,
  useUserFindOneLazyQuery,
  useUserRequestAccessMutation,
} from "gql/generated";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSendOneTimeCode } from "../SendOneTimeCodeModal";
import { getMultilingualString } from "shared/utils";

export const useAddBusinessListing = (onClose: () => void) => {
  const { toastFactory } = useToastContext();
  const [name, setName] = useState("");
  const [searchedName, setSearchedName] = useState("");
  const [selectedOperatorEntityId, setSelectedOperatorEntityId] = useState<
    string | undefined
  >(undefined);
  const [
    selectedOperatorDataForCodeModal,
    setSelectedOperatorDataForCodeModal,
  ] = useState({ _entityId: "", name: "", email: "" });
  const [isModalOpen, setIsModalOpen] = useState({
    addNewListing: false,
    sendOneTimeCode: false,
  });

  const { t } = useTranslation("core", {
    keyPrefix: "DOORSTOP.OPERATORS.ADD_MODAL",
  });

  const { refetchDbUser, dbUser } = useAppContext();

  const [getOperators, { loading: operatorsLoading, data, error, called }] =
    useOperatorManyLazyQuery();

  // useUserFindOneQuery updated to skip initial call because it was sending many
  // queries with an undefined entityId, which all failed
  const [getUser, { data: userData, loading: userLoading }] =
    useUserFindOneLazyQuery();

  const isSearchDirty =
    name !== "" && name.length >= 3 && searchedName !== name;
  const currentAccessOperatorIds = dbUser?.access.operators.map(
    (v) => v?.entityId,
  );

  const matchingOperators = data
    ? data.operatorMany
        .filter((op) => op.status !== PublicationStatusType.Deleted)
        .filter((op) => !currentAccessOperatorIds?.includes(op._entityId))
    : [];

  const isNoMatchesFound =
    called && matchingOperators.length < 1 && !operatorsLoading;
  const isNoOperatorsSelected =
    called && !operatorsLoading && !selectedOperatorEntityId;
  const isCreatingNewOperator =
    searchedName.length > 0 && (isNoMatchesFound || isNoOperatorsSelected);
  const selectedOperator = selectedOperatorEntityId
    ? matchingOperators.find((op) => op._entityId === selectedOperatorEntityId)
    : undefined;
  const isRequestingToJoin =
    selectedOperator?.claimed && !!userData && !!userData.userFindOne;

  const handleClose = () => {
    setName("");
    setSearchedName("");
    setSelectedOperatorEntityId(undefined);
    onClose();
  };

  const handleSetSelectedOperatorEntityId = (entityId: string) => {
    setSelectedOperatorEntityId(entityId);
    getUser({
      variables: {
        filter: {
          OR: [
            {
              access: {
                operators: [
                  {
                    role: EnumUserAccessOperatorsRole.Owner,
                    entityId,
                    status: EnumUserAccessOperatorsStatus.Accepted,
                  },
                ],
              },
            },
            {
              access: {
                operators: [
                  {
                    role: EnumUserAccessOperatorsRole.Staff,
                    entityId,
                    status: EnumUserAccessOperatorsStatus.Accepted,
                  },
                ],
              },
            },
          ],
        },
      },
    });
  };

  const handleSearch = () => {
    // Always reset the selected operator before new search
    setSelectedOperatorEntityId(undefined);

    getOperators({
      variables: {
        filter: { search: name },
        useCase: OperatorManyUseCaseEnum.Internal,
      },
    }).then(({ data }) => {
      setSearchedName(name);
      // If there's only one operator, automatically select it
      if (data?.operatorMany.length === 1) {
        handleSetSelectedOperatorEntityId(data.operatorMany[0]._entityId);
      }
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow searches by hitting enter
    if (event.key === "Enter" && isSearchDirty) {
      handleSearch();
    }
  };

  const handleCreateNewOperator = () => {
    handleClose();
    setIsModalOpen((prev) => {
      return { ...prev, addNewListing: true };
    });
  };

  const { onSendOneTimeCodeToUser } = useSendOneTimeCode();
  const handleSendOneTimeCode = async () => {
    const email = selectedOperator?.contact.email ?? "unknown email";
    try {
      await onSendOneTimeCodeToUser(dbUser?._id, email);
      setSelectedOperatorDataForCodeModal({
        _entityId: selectedOperatorEntityId ?? "",
        name: selectedOperator
          ? getMultilingualString(selectedOperator.name)
          : "",
        email,
      });
      handleClose();
      setIsModalOpen((prev) => ({ ...prev, sendOneTimeCode: true }));
    } catch {
      handleClose();
      toastFactory.create({
        title: t("ERROR.TITLE", { keyPrefix: "" }),
        description: t("DOORSTOP.OPERATORS.ONE_TIME_CODE_MODAL.ERROR", {
          email,
          keyPrefix: "",
        }),
      });
    }
  };

  /**
   * Handle cases when an operator is claimed AND a user has it
   * listed in their access.operators array with a status of ACCEPTED
   */
  const [userRequestAccessMutation, { loading: loadingUserRequesetAccess }] =
    useUserRequestAccessMutation();
  const handleRequestToJoin = async () => {
    if (!selectedOperatorEntityId) {
      return;
    }
    await userRequestAccessMutation({
      variables: {
        entity: {
          _entityId: selectedOperatorEntityId,
          type: EnumEntityReferenceType.Operator,
        },
      },
    })
      .then(async (response) => {
        if (response.errors) {
          toastFactory.create({
            title: t("ERROR.TITLE", { keyPrefix: "" }),
            description: t("REQUEST_TO_JOIN.ERROR"),
          });
        } else {
          toastFactory.create({
            title: t("SUCCESS", { keyPrefix: "" }),
            description: t("REQUEST_TO_JOIN.SUCCESS"),
          });
          await refetchDbUser();
          handleClose();
        }
      })
      .catch(() =>
        toastFactory.create({
          title: t("ERROR.TITLE", { keyPrefix: "" }),
          description: t("REQUEST_TO_JOIN.ERROR"),
        }),
      );
  };

  const getButtonCopy = () => {
    if (isCreatingNewOperator) {
      return t("CREATE_NEW");
    } else if (isRequestingToJoin) {
      return t("SELECTED_OP.REQUEST_TO_JOIN");
    } else if (selectedOperatorEntityId) {
      return t("SELECTED_OP.SEND_CODE");
    } else {
      return t("CONTINUE");
    }
  };

  const getOnClickHandler = () => {
    if (isCreatingNewOperator) {
      handleCreateNewOperator();
    } else if (isRequestingToJoin) {
      handleRequestToJoin();
    } else if (selectedOperatorEntityId) {
      handleSendOneTimeCode();
    } else {
      handleSearch();
    }
  };
  return {
    getButtonCopy,
    getOnClickHandler,
    setName,
    name,
    searchedName,
    matchingOperators,
    userData,
    selectedOperatorEntityId,
    selectedOperator,
    loadingUserRequesetAccess,
    operatorsLoading,
    userLoading,
    error,
    isCreatingNewOperator,
    isSearchDirty,
    isNoMatchesFound,
    isRequestingToJoin,
    handleClose,
    handleKeyDown,
    handleSetSelectedOperatorEntityId,
    isModalOpen,
    setIsModalOpen,
    selectedOperatorDataForCodeModal,
  };
};
